import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { Select, Spin, Tag } from 'antd';
import { debounce } from 'lodash';
import type { StudioCandidate } from '../contract';
import { searchStudioCandidates } from '../api';
import { mapStudioError } from '../studio-error';
import { NO_ACCOUNT_TAG } from '../labels';
import MemberCell from './member-cell';
import styles from '../styles.module.scss';

const MIN_QUERY = 2;
const HINT_SHORT = 'Nhập ít nhất 2 ký tự để tìm.';
const HINT_EMPTY = 'Không tìm thấy ai phù hợp.';

export interface MemberPickerProps {
  /** Studio đang thao tác (để tính `inThisStudio`). Bắt buộc khi người xem là Quản lý Studio. */
  studioId?: number;
  /** Chọn nhiều (tạo Studio, Admin thêm thành viên). Mặc định chọn một. */
  multiple?: boolean;
  /** Luôn là mảng (chọn một: 0–1 phần tử) để giữ đủ thông tin ứng viên (userId / uid). */
  value?: StudioCandidate[];
  onChange?: (next: StudioCandidate[]) => void;
  /**
   * Lý do KHÔNG chọn được ứng viên (hiện mờ kèm câu), null = chọn được. Mặc định: `selectable = false`
   * thì dùng `reason` của máy chủ. Ô gắn kênh truyền luật riêng (vd. chỉ ứng viên `inThisStudio`).
   */
  disabledReason?: (candidate: StudioCandidate) => string | null;
  /** Tối đa mỗi loại (tài khoản / Creator chỉ có UID) khi chọn nhiều. Mặc định 100 (S4, S11). */
  maxPerKind?: number;
  placeholder?: string;
  disabled?: boolean;
  style?: CSSProperties;
}

type PickerOption = {
  value: string;
  label: string;
  disabled: boolean;
  candidate: StudioCandidate;
  reason: string | null;
};

const defaultDisabledReason = (c: StudioCandidate) =>
  c.selectable ? null : c.reason || 'Không chọn được người này.';

const candidateLabel = (c: StudioCandidate) =>
  c.name || (c.username ? `@${c.username}` : '') || (c.uid ? `UID ${c.uid}` : c.key);

/** Giá trị gửi API từ danh sách đã chọn (S4 memberUserIds / memberUids, S11 userIds / uids). */
export function candidatesToPayload(list: StudioCandidate[]): { userIds: number[]; uids: string[] } {
  const userIds: number[] = [];
  const uids: string[] = [];
  list.forEach((c) => {
    if (c.userId !== null && c.userId !== undefined) userIds.push(c.userId);
    else if (c.uid) uids.push(c.uid);
  });
  return { userIds, uids };
}

/**
 * Ô chọn thành viên dùng S26 `GET /studios/candidates?q=&studioId=` (mục 4.7, 8.6): tìm theo tên, TikTok ID
 * hoặc UID trên CẢ tài khoản app lẫn Creator chỉ có UID trong file (không phải gõ tay UID). Không có email.
 * Mỗi dòng: tên, TikTok ID, UID, thẻ "Chưa có tài khoản", "Đang ở: <Studio>"; ứng viên không chọn được hiện
 * mờ kèm lý do.
 */
const MemberPicker = ({
  studioId,
  multiple = false,
  value,
  onChange,
  disabledReason = defaultDisabledReason,
  maxPerKind = 100,
  placeholder = 'Tìm theo tên, TikTok ID hoặc UID',
  disabled,
  style,
}: MemberPickerProps) => {
  const [found, setFound] = useState<StudioCandidate[]>([]);
  const [fetching, setFetching] = useState(false);
  const [hint, setHint] = useState(HINT_SHORT);
  const seqRef = useRef(0);
  const selected = useMemo(() => value || [], [value]);

  const search = useMemo(
    () =>
      debounce(async (text: string) => {
        const seq = ++seqRef.current;
        try {
          const list = await searchStudioCandidates(text, studioId);
          if (seq !== seqRef.current) return;
          const rows = Array.isArray(list) ? list : [];
          setFound(rows);
          setHint(rows.length ? '' : HINT_EMPTY);
        } catch (err) {
          if (seq !== seqRef.current) return;
          setFound([]);
          setHint(mapStudioError(err));
        } finally {
          if (seq === seqRef.current) setFetching(false);
        }
      }, 400),
    [studioId]
  );

  useEffect(() => () => search.cancel(), [search]);

  const onSearch = (text: string) => {
    if (text.trim().length < MIN_QUERY) {
      search.cancel();
      seqRef.current += 1;
      setFetching(false);
      setFound([]);
      setHint(HINT_SHORT);
      return;
    }
    setFetching(true);
    search(text);
  };

  const accountCount = selected.filter((c) => c.userId !== null && c.userId !== undefined).length;
  const uidCount = selected.length - accountCount;
  const selectedKeys = useMemo(() => selected.map((c) => c.key), [selected]);

  const options: PickerOption[] = useMemo(() => {
    const byKey = new Map<string, StudioCandidate>();
    selected.forEach((c) => byKey.set(c.key, c));
    found.forEach((c) => {
      if (!byKey.has(c.key)) byKey.set(c.key, c);
    });
    const list: PickerOption[] = [];
    byKey.forEach((c) => {
      const isSelected = selectedKeys.includes(c.key);
      let reason = isSelected ? null : disabledReason(c);
      if (!reason && !isSelected && multiple) {
        const isAccount = c.userId !== null && c.userId !== undefined;
        if (isAccount && accountCount >= maxPerKind) reason = `Tối đa ${maxPerKind} tài khoản.`;
        if (!isAccount && uidCount >= maxPerKind)
          reason = `Tối đa ${maxPerKind} Creator chưa có tài khoản.`;
      }
      list.push({ value: c.key, label: candidateLabel(c), disabled: !!reason, candidate: c, reason });
    });
    return list;
  }, [
    selected,
    found,
    selectedKeys,
    disabledReason,
    multiple,
    accountCount,
    uidCount,
    maxPerKind,
  ]);

  const handleChange = (next: string | string[] | undefined) => {
    const keys = Array.isArray(next) ? next : next ? [next] : [];
    const lookup = new Map<string, StudioCandidate>();
    options.forEach((o) => lookup.set(o.value, o.candidate));
    const list = keys
      .map((key) => lookup.get(key))
      .filter((c): c is StudioCandidate => !!c);
    onChange?.(list);
  };

  return (
    <Select<string | string[], PickerOption>
      mode={multiple ? 'multiple' : undefined}
      showSearch
      allowClear
      filterOption={false}
      disabled={disabled}
      placeholder={placeholder}
      value={multiple ? selectedKeys : selectedKeys[0]}
      onSearch={onSearch}
      onChange={handleChange}
      options={options}
      style={{ width: '100%', ...style }}
      notFoundContent={
        fetching ? (
          <Spin size="small" />
        ) : hint ? (
          <span className={styles.pickerReason}>{hint}</span>
        ) : null
      }
      optionRender={(option) => {
        const data = option.data as PickerOption;
        const c = data.candidate;
        return (
          <div className={styles.pickerOption}>
            <MemberCell
              name={c.name}
              username={c.username}
              uid={c.uid}
              avatar={c.avatar}
              size={28}
              sub={[c.username ? `@${c.username}` : '', c.uid ? `UID ${c.uid}` : '']
                .filter(Boolean)
                .join(' · ')}
              extra={
                <>
                  {!c.hasAccount && <Tag>{NO_ACCOUNT_TAG}</Tag>}
                  {c.currentStudio && (
                    <Tag color={c.inThisStudio ? 'green' : 'default'}>
                      Đang ở: {c.currentStudio.title || `Studio #${c.currentStudio.id}`}
                    </Tag>
                  )}
                </>
              }
            />
            {data.reason && <span className={styles.pickerReason}>{data.reason}</span>}
          </div>
        );
      }}
    />
  );
};

export default MemberPicker;
